import {FC, Fragment, useCallback, useEffect, useMemo} from 'react';
import {observer} from 'mobx-react';
import {Message, Radio, Table} from 'semantic-ui-react';
import {Field, ValidationErrors, ValueRenderer, ValueRendererProps} from 'apstra-ui-common';
import {map, transform, uniqueId, isEmpty, isEqual, isNil, every, forEach, castArray, merge, get} from 'lodash';
import type {JSONSchema7} from 'json-schema';

import {Probe, ProbeProcessor, ProcessorDefinition} from '../types';
import {
  buildColumnFilterSchema, normalizeColumnFilterValue, ColumnFilterValue, ColumnFilterSchema, filterSectionsSchema
} from '../processorUtils/select';

import './ColumnFilter.less';

const useColumnFilterSchema = (
  value, probe: Probe, processor: ProbeProcessor, processorDefinitionsByName: Record<string, ProcessorDefinition>
): [ColumnFilterSchema, ColumnFilterValue] => {
  const {stage: inputStageName, column: inputColumnName} = processor.inputs.in ?? {};
  const filterSchema = useMemo(
    () => buildColumnFilterSchema(probe, inputStageName, inputColumnName, processorDefinitionsByName),
    [probe, inputStageName, inputColumnName, processorDefinitionsByName]
  );
  const filterValue = useMemo(
    () => normalizeColumnFilterValue(value, filterSchema),
    [filterSchema, value]
  );
  return [filterSchema, filterValue];
};

type ColumnFilterProps = {
  value: ColumnFilterValue;
  schema?: JSONSchema7;
  errors?: any[];
  probe: Probe;
  processor: ProbeProcessor;
  processorDefinitionsByName: Record<string, ProcessorDefinition>;
}

const ColumnFilter: FC<ColumnFilterProps> = observer(({
  value: originalFilterValue, probe, processor, processorDefinitionsByName
}) => {
  const [filterSchema, filterValue] = useColumnFilterSchema(
    originalFilterValue, probe, processor, processorDefinitionsByName
  );

  const tableSchema = useMemo(
    () => transform(
      filterSectionsSchema, (result, {title: sectionTitle, columnTitles, values, defaultValue}, sectionName) => {
        forEach(values, (possibleValue, possibleValueIndex) => {
          if (possibleValue === 'exclude') return;
          forEach(filterSchema[sectionName], ({name, title}) => {
            const actualValue = filterValue[sectionName][name] ?? defaultValue;
            if (actualValue === possibleValue) {
              (result[sectionTitle + ': ' + columnTitles[possibleValueIndex]] ??= []).push(title ?? name);
            }
          });
        });
      }, {}
    ),
    [filterSchema, filterValue]
  );

  if (every(tableSchema, isEmpty)) return 'Empty';

  return (
    <Table basic='very' compact collapsing>
      <Table.Body>
        {map(tableSchema, (columnTitles, rowTitle) =>
          <Table.Row key={rowTitle}>
            <Table.Cell collapsing>
              {rowTitle}
            </Table.Cell>
            <Table.Cell>
              {map(columnTitles, (columnTitle, index) =>
                <Fragment key={columnTitle}>
                  {index !== 0 && ', '}
                  <b>{columnTitle}</b>
                </Fragment>
              )}
            </Table.Cell>
          </Table.Row>
        )}
      </Table.Body>
    </Table>
  );
});

type ColumnFilterEditorProps = ColumnFilterProps & {
  onChange: (value: any) => void;
  disabled?: boolean;
}

export const ColumnFilterEditor: FC<ColumnFilterEditorProps> = observer(({
  schema, value: originalFilterValue, onChange, errors: originalErrors, disabled,
  probe, processor, processorDefinitionsByName,
}) => {
  const tableId = useMemo(() => uniqueId('column-filter-'), []);

  const [filterSchema, filterValue] = useColumnFilterSchema(
    originalFilterValue, probe, processor, processorDefinitionsByName
  );

  useEffect(() => {
    if (!isEqual(originalFilterValue, filterValue)) onChange(filterValue);
  }, [originalFilterValue, filterValue, onChange]);

  const onColumnValueChange = useCallback((sectionName, columnName, value) => {
    const newSection = {...filterValue[sectionName]};
    if (value === filterSectionsSchema[sectionName].defaultValue) {
      delete newSection[columnName];
    } else {
      newSection[columnName] = value;
    }
    onChange({...filterValue, [sectionName]: newSection});
  }, [filterValue, onChange]);

  const errors = useMemo(() => merge({}, ...(originalErrors ?? [])), [originalErrors]);

  if (every(filterSchema, isEmpty)) {
    return (
      <Message
        info
        icon='info circle'
        className='column-filter-message'
        content='No columns available'
      />
    );
  }

  return map(filterSectionsSchema, ({title: sectionTitle, columnTitles, values, defaultValue}, sectionName) => {
    const sectionId = `${tableId}-${sectionName}`;
    return !isEmpty(filterSchema[sectionName]) ? (
      <div key={sectionName} className='column-filter-section'>
        <div className='column-filter-section-header' id={sectionId}>{sectionTitle}</div>
        <div className='form-text'>
          {get(schema, ['properties', sectionName, 'additionalProperties', 'description'])}
        </div>
        <Table fixed definition size='small' className='column-filter'>
          <Table.Body>
            <Table.Row>
              <Table.Cell />
              {map(columnTitles, (columnTitle, columnIndex) =>
                <Table.Cell
                  key={columnTitle}
                  id={`${sectionId}-${values[columnIndex]}`}
                >
                  {columnTitle}
                </Table.Cell>
              )}
            </Table.Row>
            {map(filterSchema[sectionName], ({name: columnName, title: columnTitle}) => {
              const columnErrors = errors[sectionName]?.[columnName];
              return (
                <Table.Row key={columnName} error={!isEmpty(columnErrors)}>
                  <Table.Cell id={`${sectionId}-${columnName}`}>
                    <div>{columnTitle ?? columnName}</div>
                    {!isEmpty(columnErrors) &&
                      <ValidationErrors errors={castArray(columnErrors)} />
                    }
                  </Table.Cell>
                  {map(values, (value) => (
                    <Table.Cell key={value}>
                      <Radio
                        name={columnName}
                        disabled={disabled}
                        checked={
                          value === defaultValue && isNil(filterValue[sectionName][columnName]) ||
                          filterValue[sectionName][columnName] === value
                        }
                        onChange={() => onColumnValueChange(sectionName, columnName, value)}
                        aria-labelledby={[sectionId, `${sectionId}-${columnName}`, `${sectionId}-${value}`].join(' ')}
                      />
                    </Table.Cell>
                  ))}
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      </div>
    ) : null;
  });
});

export const columnFilterRenderer = new ValueRenderer({
  condition: ({name, schema}) => name === 'column_filter' && schema?.type === 'object',
  Value: ({value, schema, errors, probe, processor, processorDefinitionsByName}) => {
    return (
      <ColumnFilter
        value={value}
        schema={schema}
        errors={errors}
        probe={probe}
        processor={processor}
        processorDefinitionsByName={processorDefinitionsByName}
      />
    );
  },
  ValueInput: ({
    name, value, schema, required, disabled, errors, onChange, fieldProps,
    probe, processor, processorDefinitionsByName
  }) => {
    return (
      <Field
        label={schema?.title ?? name}
        required={required}
        disabled={disabled}
        {...fieldProps}
      >
        <ColumnFilterEditor
          value={value}
          schema={schema}
          disabled={disabled}
          onChange={onChange}
          errors={errors}
          probe={probe}
          processor={processor}
          processorDefinitionsByName={processorDefinitionsByName}
        />
      </Field>
    );
  },
});

const ColumnFilterExample: FC<ValueRendererProps> = observer(({value: filterValue}) => {
  const tableSchema = transform(
    filterSectionsSchema, (result, {title: sectionTitle, columnTitles, values, defaultValue}, sectionName) => {
      forEach(values, (possibleValue, possibleValueIndex) => {
        forEach(filterValue[sectionName], (value, valueName) => {
          const actualValue = value ?? defaultValue;
          if (actualValue === possibleValue) {
            (result[sectionTitle + ': ' + columnTitles[possibleValueIndex]] ??= []).push(valueName);
          }
        });
      });
    }, {} as Record<string, string[]>
  );

  return map(tableSchema, (columnTitles, rowTitle) =>
    <div key={rowTitle}>
      {rowTitle}{': '}{columnTitles.join(', ')}
    </div>
  );
});

export const columnFilterExampleRenderer = new ValueRenderer({
  condition: ({name}) => name === 'column_filter',
  Value: ColumnFilterExample,
});
